import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface PartnerCompany {
  id: string;
  name: string;
  logoUrl: string | null;
  companyType: string;
  companyTypeName: string;
}

export interface TenantMembership {
  tenantId: string;
  tenantName: string;
  tenantLogoUrl: string | null;
  role: string;
  participationRole: string | null;
  capabilities: string[];
}

export interface EffectiveCapabilities {
  can_deploy_workers?: boolean;
  can_request_ptw?: boolean;
  can_create_ptw?: boolean;
  can_approve_ptw?: boolean;
  can_approve_timesheets?: boolean;
  can_report_incidents?: boolean;
  can_own_investigation?: boolean;
  can_raise_non_conformances?: boolean;
  can_verify_worker_identity?: boolean;
  [key: string]: boolean | undefined;
}

export interface PortalPreferences {
  theme?: string;
  language?: string;
  dismissed_checklist_items?: string[];
  /** Pack 12.D — partner customisation surface (accent color hex, e.g. `#2563eb`). */
  accentColor?: string;
  /** Pack 12.D — uploaded logo URL when the partner customizes their portal branding. */
  customLogoUrl?: string;
  /** Pack 12.D — optional display-name override shown in the top bar. */
  customPortalName?: string;
  /** Pack 8.B — billing contact details persisted into preferences JSONB. */
  billingContact?: { name?: string; email?: string; phone?: string };
  [key: string]: unknown;
}

export interface TenantInvitation {
  id: string;
  targetTenantId: string;
  tenantName: string;
  tenantLogoUrl: string | null;
  createdAt: string | null;
  notes: string | null;
  invitedByName: string | null;
  companyTypeName: string | null;
}

export interface PartnerContext {
  company: PartnerCompany | null;
  companyType: string;
  companyTypeName: string;
  companyLogoUrl: string | null;
  activeTenantId: string | null;
  activeTenantName: string | null;
  tenantMemberships: TenantMembership[];
  effectiveCapabilities: EffectiveCapabilities;
  entitlements: Record<string, number | boolean | string | null>;
  portalPreferences: PortalPreferences;
  isUnassigned: boolean;
  isPartnerCompanyUser: boolean;
  pendingAccessRequests: number;
  pendingTenantInvitations: TenantInvitation[];
}

export interface UsePartnerContextReturn extends PartnerContext {
  isLoading: boolean;
  error: Error | null;
  switchTenant: (tenantId: string) => Promise<void>;
}

// TRI-092 (Task #2054) — PARTNER_ACTIVE_TENANT_KEY has been fully removed.
// The localStorage shadow key `partner_active_tenant` was retired when the
// active tenant moved to server-side session state. All callers have been
// updated; the cleanup block is no longer needed.

async function fetchPartnerContext(): Promise<PartnerContext> {
  const res = await fetch("/api/partner/context", { credentials: "include" });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || "Failed to load partner context");
  }
  return res.json();
}

const FALLBACK_CONTEXT: PartnerContext = {
  company: null,
  companyType: "",
  companyTypeName: "",
  companyLogoUrl: null,
  activeTenantId: null,
  activeTenantName: null,
  tenantMemberships: [],
  effectiveCapabilities: {},
  entitlements: {},
  portalPreferences: {},
  isUnassigned: true,
  isPartnerCompanyUser: false,
  pendingAccessRequests: 0,
  pendingTenantInvitations: [],
};

/**
 * Pack 9.A: low-level fetcher for the partner-context payload. Materialized
 * once by `PartnerContextProvider`; also used as the no-provider fallback by
 * `usePartnerContext`. The query key is shared so the React Query cache acts
 * as a second-tier deduplication layer (matters when two parts of the tree
 * mount during a tenant switch and the provider is briefly remounted).
 */
export function usePartnerContextQuery(opts?: { enabled?: boolean }): UsePartnerContextReturn {
  const { data, isLoading, error } = useQuery<PartnerContext, Error>({
    queryKey: ["partner-context"],
    queryFn: fetchPartnerContext,
    staleTime: 30_000,
    retry: 1,
    enabled: opts?.enabled ?? true,
  });

  const switchTenant = useSwitchPartnerTenant();

  return {
    ...(data ?? FALLBACK_CONTEXT),
    isLoading,
    error: error ?? null,
    switchTenant,
  };
}

export function useSwitchPartnerTenant() {
  const queryClient = useQueryClient();
  // Tech Debt J — the active tenant is server-state only. The mutation just
  // POSTs the desired tenant to the server (which writes
  // `req.session.activeTenantId` and refreshes the partner-context cache),
  // and we invalidate the React Query cache so the next render reads the
  // freshly server-pushed value. There is no client-side shadow to keep in
  // sync, so there is also no rollback handling for it on error.
  const switchTenantMutation = useMutation({
    mutationFn: async ({ tenantId }: { tenantId: string }) => {
      await apiRequest("POST", "/api/partner/switch-tenant", { tenantId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-context"] });
    },
  });

  return async (tenantId: string) => {
    await switchTenantMutation.mutateAsync({ tenantId });
  };
}
